import { isPhaseLabelOwnedByExcluded } from "./bundle-ownership";
import {
  ScopeGateBundleOverride,
  ScopeGateConfig,
  ScopeGateThresholds,
} from "../agent-config-options";

/**
 * Classified scope size for a single issue. Drives the dispatch
 * decision in the orchestrator — `small` / `medium` are dispatched,
 * `large` is rejected and decomposed.
 */
export const SCOPE_CLASS_VALUES = ["small", "medium", "large"] as const;
export type ScopeClass = (typeof SCOPE_CLASS_VALUES)[number];

/**
 * Default acceptance-criteria thresholds — openhi's published
 * heuristic. An issue with at most 3 acceptance-criteria checkboxes
 * is `small`; at most 6 is `medium`; more than 6 is `large`.
 */
export const DEFAULT_AC_THRESHOLDS: ScopeGateThresholds = {
  smallMax: 3,
  mediumMax: 6,
};

/**
 * Default sources thresholds — openhi's published heuristic. An
 * issue with at most 2 listed sources is `small`; at most 5 is
 * `medium`; more than 5 is `large`.
 */
export const DEFAULT_SOURCES_THRESHOLDS: ScopeGateThresholds = {
  smallMax: 2,
  mediumMax: 5,
};

/**
 * Bundle overrides shipped out of the box for content-spec workflows
 * — issues whose AC list is the per-section content checklist for
 * one cohesive deliverable (not a phase-completion checklist that
 * can be decomposed). The global default cap (`mediumMax: 6`) is
 * calibrated for phased-bundle issues and systematically over-flags
 * single-document writes.
 *
 * The shipped overrides:
 *
 * - `req:write` — formal requirement documents (ADR / TR / OPS /
 *   SEC / NFR / UX / MT) routinely carry 12–20 ACs covering
 *   per-section content invariants (definition, alternatives,
 *   risks, traceability, revision history, open items). Cap:
 *   `mediumMax: 20` on the AC axis; sources unchanged.
 * - `bcm:scaffold` — multi-section BCM documents compress sub-
 *   addendum requirements into coarse ACs but still land above
 *   the global cap. Cap: `mediumMax: 12` on the AC axis; sources
 *   unchanged.
 * - `research:verify` — the `research-analyst` slice phase emits
 *   a fixed-shape verify template. A 32-issue cohort observed in
 *   `codedrifters/openhi-planning` (2026-05-04) all landed at
 *   identical `ac=9 / src=2` dimensions, so the template's shape
 *   itself is what trips the global cap. Cap: `mediumMax: 12` on
 *   the AC axis (max observed 9 + ~3 headroom); sources unchanged.
 * - `research:scope` — the `research-analyst` Phase-1 scope phase
 *   decomposes a research question and writes a single scope file
 *   whose ACs are the per-section content checklist for that one
 *   deliverable (question decomposition, per-slice briefs, and the
 *   acceptance criteria for the final deliverable), not a phase-
 *   completion checklist that can be decomposed. Sibling of
 *   `research:verify` in the same pipeline; observed ac=7
 *   (`codedrifters/openhi-planning`#6034). Cap: `mediumMax: 12` on
 *   the AC axis (matching the `research:verify` sibling); sources
 *   unchanged.
 * - `software:profile` — software-product profile pages enumerate
 *   per-section content invariants (overview, vendor, integrations,
 *   pricing, security posture, …). Max observed 11 ACs in the same
 *   openhi-planning sample; cap: `mediumMax: 14` on the AC axis
 *   (max observed + 3 headroom); sources unchanged.
 * - `software:research` — the `software-profile` Phase-1 research
 *   phase gathers public sources and writes a single bounded
 *   research-notes file whose ACs are the per-section content
 *   checklist for that one deliverable, not a decomposable plan.
 *   Sibling of `software:profile` in the same pipeline; observed
 *   ac=8 (`codedrifters/openhi-planning`#6259). Cap: `mediumMax: 14`
 *   on the AC axis (matching the `software:profile` sibling);
 *   sources unchanged.
 * - `regulatory:research` — single regulation pages carry per-
 *   section ACs covering jurisdiction, scope, obligations,
 *   penalties, and effective dates. Single-document conformance
 *   scans run ac=11; cap: `mediumMax: 12` on the AC axis;
 *   sources unchanged.
 * - `regulatory:impact` — the `regulatory-research` Phase-3 impact
 *   phase is synthesis-only (no web searches): it appends a single
 *   `## Impact` section to the regulation page — product-impact
 *   assessment, capability-gap enumeration, and obligation hand-offs
 *   — whose ACs are the per-section content checklist for that one
 *   document section. Sibling of `regulatory:research`; observed
 *   ac=9 (`codedrifters/openhi-planning`#3505). Cap: `mediumMax: 10`
 *   on the AC axis (observed max 9 + headroom; the tighter cap
 *   versus the research sibling's 12 reflects the narrower
 *   single-section deliverable); sources unchanged.
 * - `standards:research` — single standard-version research notes
 *   enumerate one cohesive deliverable across per-section ACs
 *   (candidate encodings, reconciliation, storage/query analysis,
 *   citations, recommendation). Sibling of `regulatory:research`
 *   with the same content-spec note shape. Max observed 7 ACs
 *   (`codedrifters/openhi-planning`#5994); cap: `mediumMax: 10` on
 *   the AC axis (max observed + 3 headroom, matching the
 *   `regulatory:research` sibling); sources unchanged.
 * - `software:map` — capability-mapping output is a single cohesive
 *   matrix file whose ACs enumerate per-row/per-column requirements
 *   and whose Sources block typically cross-references the entire
 *   BCM tree slice. Max observed `ac=9 / src=13`; cap:
 *   `acceptanceCriteria.mediumMax: 12` (max + 3) and
 *   `sources.mediumMax: 15` (max + 2 — sources headroom is tighter
 *   because BCM cross-refs naturally cap out near the slice size).
 * - `bcm:connect` — connect-phase outputs cross-link a capability
 *   to upstream value streams, downstream profiles, and adjacent
 *   capabilities. Max observed `ac=9 / src=6`; cap:
 *   `acceptanceCriteria.mediumMax: 12` (max + 3) and
 *   `sources.mediumMax: 8` (max + 2).
 * - `software:matrix` — physician-RCM-style feature matrices
 *   (per-row/per-column requirements for one cohesive matrix
 *   file). The shape mirrors `software:map`: single-cohesive-
 *   file matrix output whose ACs enumerate per-row/per-column
 *   requirements rather than phase milestones. Mirrors the
 *   `software:map` thresholds verbatim (closest-peer rationale,
 *   surfaced by openhi-planning#3223).
 * - `people:research` / `company:research` — profile research
 *   phases whose AC list is the per-section content checklist for
 *   one cohesive research-notes file (sources by tier, conflict
 *   resolution, dated claims). Not a phase-completion checklist
 *   that can be decomposed. Cap: `mediumMax: 12` on the AC axis;
 *   sources unchanged.
 * - `people:draft` / `company:draft` — profile draft phases that
 *   read the research notes and write one cohesive structured
 *   profile document. The per-section AC checklist (overview,
 *   leadership, history, sources, …) is the content spec for that
 *   single document, not a decomposable plan. Cap: `mediumMax: 12`
 *   on the AC axis; sources unchanged.
 * - `business-models:canvas` / `business-models:complete` — the
 *   business-models pipeline authors one cohesive Business Model
 *   Canvas document per segment across two content-spec phases that
 *   edit the SAME `business-model.md` file. The canvas phase writes
 *   the nine Osterwalder blocks + segment index + downstream handoff;
 *   the complete phase appends BIZBOK value streams, size / geographic
 *   / regulatory variations, and the value-stream-to-capability
 *   linking table. Both AC lists are the per-section content checklist
 *   for that single indivisible document, not a phase-completion
 *   checklist that can be decomposed — a single canvas cannot be split
 *   into sequential sub-issues without every phase editing the same
 *   file. A representative canvas issue observed at ac=7
 *   (`codedrifters/openhi-planning`#13613). Cap: `mediumMax: 12` on the
 *   AC axis for both (matching the `bcm:*` / `company:research`
 *   document-authoring norm; the complete phase is a multi-section
 *   append, so it takes the same cap as its canvas sibling rather than
 *   a tighter single-section cap); sources unchanged.
 * - `req:draft-trace` — the `requirements-analyst` draft-trace
 *   phase emits a single proposal-with-traceability document whose
 *   ACs enumerate per-section content invariants. Sibling of
 *   `req:write`; mirrors its `mediumMax: 20` AC cap (observed max
 *   ac=9, headroom intentional). Sources unchanged.
 * - `meeting:notes` — Phase-2 meeting-notes output is a single
 *   cohesive notes document whose ACs enumerate per-section
 *   content invariants. Cap: `mediumMax: 9` on the AC axis;
 *   sources unchanged.
 * - `meeting:draft` / `meeting:link` — Phase-3/Phase-4 meeting
 *   outputs carry a "file N action-item issues" criterion plus
 *   per-section content ACs, and cross-reference every session
 *   input. Cap: `acceptanceCriteria.mediumMax: 15` and
 *   `sources.mediumMax: 10` on both axes.
 * - `type:docs` — single-document doc writes whose AC list is the
 *   per-section content outline for one cohesive page, not a
 *   decomposable plan. This is the first override keyed on a
 *   `type:*` label rather than a phase label; it matches because
 *   the scope gate resolves overrides against every label on the
 *   issue (see `resolveOverrideForLabels`). Cap: `mediumMax: 12`
 *   on the AC axis; sources unchanged (global band).
 * - `bcm:context` — the `bcm-writer` context phase authors a
 *   single BCM context document whose AC list is a per-section
 *   content checklist (definitions, boundaries, sub-capabilities,
 *   value-stream links), not a decomposable plan. Sibling of
 *   `bcm:scaffold` / `bcm:connect`; mirrors their `mediumMax: 12`
 *   AC cap. AC axis only; sources fall through to the global band.
 * - `req:scan` — the `requirements-analyst` Phase-1 dedup scan
 *   produces one cohesive scan report, but a dedup pass inherently
 *   reads MANY existing requirements, ADRs, and open issues, so it
 *   trips BOTH the AC ceiling AND the sources ceiling (evidence
 *   issue scored `ac=7 / src=9`). Both axes are raised to
 *   `mediumMax: 12` — this is why `req:scan` carries a `sources`
 *   override the AC-only siblings do not.
 * - `bcm:outline` — the `bcm-writer` Phase-1 outline phase authors a
 *   single capability outline document whose AC list is the content
 *   checklist for that one indivisible deliverable (identify the L1
 *   parent, draft the L2 definition, document sub-facets, draw the
 *   capability boundary, write the outline file, update the
 *   capability map), not a decomposable plan — the criteria cannot be
 *   split into independently-mergeable sub-issues because every one
 *   of them edits the same outline file. Sibling of `bcm:scaffold` /
 *   `bcm:context` / `bcm:connect`; mirrors their `mediumMax: 12` AC
 *   cap. Observed ac=7 (`codedrifters/openhi-planning`#11216). AC
 *   axis only; sources fall through to the global band.
 *
 * Consumer overrides deep-merge with these defaults with
 * consumer-wins-per-key (see `resolveScopeGate` for the merge
 * semantics).
 */
export const DEFAULT_BUNDLE_OVERRIDES: {
  readonly [phaseLabel: string]: ResolvedScopeGateBundleOverride;
} = {
  "req:write": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 20 },
  },
  "bcm:scaffold": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "research:verify": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "research:scope": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "software:profile": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 14 },
  },
  "software:research": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 14 },
  },
  "regulatory:research": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "regulatory:impact": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 10 },
  },
  "standards:research": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 10 },
  },
  "software:map": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
    sources: { smallMax: 2, mediumMax: 15 },
  },
  "bcm:connect": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
    sources: { smallMax: 2, mediumMax: 8 },
  },
  "software:matrix": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
    sources: { smallMax: 2, mediumMax: 15 },
  },
  "people:research": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "company:research": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "people:draft": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "company:draft": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "business-models:canvas": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "business-models:complete": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "req:draft-trace": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 20 },
  },
  "meeting:notes": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 9 },
  },
  "meeting:draft": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 15 },
    sources: { smallMax: 2, mediumMax: 10 },
  },
  "meeting:link": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 15 },
    sources: { smallMax: 2, mediumMax: 10 },
  },
  "type:docs": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "bcm:context": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
  "req:scan": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
    sources: { smallMax: 2, mediumMax: 12 },
  },
  "bcm:outline": {
    acceptanceCriteria: { smallMax: 3, mediumMax: 12 },
  },
};

/**
 * Resolved per-phase-label override. At least one of the two axis
 * fields is populated — empty resolved overrides are dropped
 * rather than retained, so callers can assume any entry in the
 * resolved override map carries actionable thresholds.
 *
 * Each axis here is **fully resolved** (`smallMax` + `mediumMax`).
 * The override resolver fills in the missing axis from the global
 * resolved thresholds at the time `resolveScopeGate` runs.
 */
export interface ResolvedScopeGateBundleOverride {
  readonly acceptanceCriteria?: ScopeGateThresholds;
  readonly sources?: ScopeGateThresholds;
}

/**
 * Default decomposition-proposal comment body. The orchestrator
 * substitutes the following angle-bracketed uppercase-snake
 * placeholders at comment-composition time:
 *
 * - `<AC_COUNT>` — observed acceptance-criteria count.
 * - `<SOURCES_COUNT>` — observed sources count.
 * - `<AC_LIMIT>` — the `acceptanceCriteria.mediumMax` threshold.
 * - `<SOURCES_LIMIT>` — the `sources.mediumMax` threshold.
 *
 * The placeholder syntax deliberately avoids `{{curly-brace}}` form —
 * `AgentConfig`'s template resolver claims that namespace at rule
 * generation time, so a `{{acCount}}` placeholder would be rewritten
 * to `<acCount>` before the agent ever saw it.
 *
 * This template is deliberately generic so every consuming repo
 * can adopt it without customization — project-specific phrasing
 * belongs in `ScopeGateConfig.decompositionTemplate`.
 */
export const DEFAULT_DECOMPOSITION_TEMPLATE = [
  "## Scope gate: issue is too large to dispatch",
  "",
  "The orchestrator rejected this issue at dispatch because it",
  "exceeds the configured scope thresholds:",
  "",
  "- Acceptance criteria: **<AC_COUNT>** (limit for single-session work: **<AC_LIMIT>**)",
  "- Sources referenced: **<SOURCES_COUNT>** (limit for single-session work: **<SOURCES_LIMIT>**)",
  "",
  "### Proposed phased decomposition",
  "",
  "Split the work into sequential phases and file each phase as its",
  "own sub-issue. Each sub-issue should land at or below the",
  "`small` / `medium` scope thresholds so it dispatches normally on",
  "the next orchestrator run.",
  "",
  "- **Phase 1 — <short title>.** <1 sentence scope + 1–3 AC>",
  "- **Phase 2 — <short title>.** <1 sentence scope + 1–3 AC>",
  "  *Depends on: #<phase-1-issue>*",
  "- **Phase 3 — <short title>.** <1 sentence scope + 1–3 AC>",
  "  *Depends on: #<phase-2-issue>*",
  "",
  "After the sub-issues are filed, this issue stays `status:blocked`",
  "with a `Depends on: #<phase-1>, #<phase-2>, #<phase-3>` line in its",
  "body so the unblock loop closes it automatically once every phase",
  "merges. The `status:needs-attention` label is applied so a human",
  "can confirm the decomposition before work starts.",
].join("\n");

/**
 * Fully-resolved scope-gate settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 *
 * `bundleOverrides` is the deep-merged result of the shipped
 * `DEFAULT_BUNDLE_OVERRIDES` and any consumer-supplied
 * `ScopeGateConfig.bundleOverrides`. Consumer entries replace
 * shipped defaults per-key; entries set to `undefined` opt out of
 * the shipped default for that key. The resolved map only contains
 * entries whose merged value carries at least one axis — empty
 * overrides are dropped.
 */
export interface ResolvedScopeGate {
  readonly enabled: boolean;
  readonly acceptanceCriteria: ScopeGateThresholds;
  readonly sources: ScopeGateThresholds;
  readonly autoFile: boolean;
  readonly decompositionTemplate: string;
  readonly bundleOverrides: {
    readonly [phaseLabel: string]: ResolvedScopeGateBundleOverride;
  };
}

/**
 * Resolve a (possibly absent) `ScopeGateConfig` into a canonical
 * `ResolvedScopeGate` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs (negative or non-integer thresholds, inverted
 * ranges) throw a descriptive `Error` — callers should not need to
 * guard against it at runtime.
 */
export function resolveScopeGate(config?: ScopeGateConfig): ResolvedScopeGate {
  const ac = resolveThresholds(
    config?.acceptanceCriteria,
    DEFAULT_AC_THRESHOLDS,
    "acceptanceCriteria",
  );
  const sources = resolveThresholds(
    config?.sources,
    DEFAULT_SOURCES_THRESHOLDS,
    "sources",
  );
  const bundleOverrides = resolveBundleOverrides(
    config?.bundleOverrides,
    DEFAULT_BUNDLE_OVERRIDES,
  );

  return {
    enabled: config?.enabled ?? true,
    acceptanceCriteria: ac,
    sources,
    autoFile: config?.autoFile ?? false,
    decompositionTemplate:
      config?.decompositionTemplate ?? DEFAULT_DECOMPOSITION_TEMPLATE,
    bundleOverrides,
  };
}

/**
 * Effective thresholds applied to a single issue. Produced by
 * `resolveOverrideForLabels` so the classifier (and the shell helper)
 * can use a single shape regardless of whether an override matched.
 */
export interface EffectiveScopeThresholds {
  readonly acceptanceCriteria: ScopeGateThresholds;
  readonly sources: ScopeGateThresholds;
  readonly matchedLabel?: string;
}

/**
 * Pick the effective thresholds for an issue carrying `labels`.
 * Walks the resolved bundle-override map, finds every label whose
 * exact name appears as a key, and selects the **first match in
 * alphabetical order on the label name**. The override's
 * `acceptanceCriteria` and `sources` axes are independent — the
 * unspecified axis falls through to the global resolved
 * thresholds.
 *
 * When no label matches, returns the global thresholds with no
 * `matchedLabel`.
 */
export function resolveOverrideForLabels(
  gate: ResolvedScopeGate,
  labels: ReadonlyArray<string>,
): EffectiveScopeThresholds {
  const overrideKeys = Object.keys(gate.bundleOverrides);
  if (overrideKeys.length === 0 || labels.length === 0) {
    return {
      acceptanceCriteria: gate.acceptanceCriteria,
      sources: gate.sources,
    };
  }
  // Sort the issue's labels alphabetically and pick the first one
  // that matches an override key. This makes tie-breaking
  // deterministic when multiple labels could match.
  const sortedLabels = [...labels].sort((a, b) => a.localeCompare(b));
  const matchedLabel = sortedLabels.find((label) =>
    Object.prototype.hasOwnProperty.call(gate.bundleOverrides, label),
  );
  if (matchedLabel === undefined) {
    return {
      acceptanceCriteria: gate.acceptanceCriteria,
      sources: gate.sources,
    };
  }
  const override = gate.bundleOverrides[matchedLabel];
  return {
    acceptanceCriteria: override.acceptanceCriteria ?? gate.acceptanceCriteria,
    sources: override.sources ?? gate.sources,
    matchedLabel,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `ScopeGateConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * gate fails the build instead of silently shipping broken dispatch
 * thresholds. Returns the resolved gate unchanged so callers can
 * write `const gate = validateScopeGateConfig(config)` in one line.
 *
 * Malformed cases rejected here:
 *
 * - Threshold values that are negative or non-integer.
 * - Threshold ranges where `mediumMax <= smallMax` (the gate would
 *   never classify an issue as `medium`).
 */
export function validateScopeGateConfig(
  config?: ScopeGateConfig,
): ResolvedScopeGate {
  // resolveScopeGate does the numeric validation. We call it here
  // so the same error messages surface whether the caller used the
  // resolver or the validator entry point.
  return resolveScopeGate(config);
}

/**
 * Classify a raw issue body against a resolved scope gate. Returns
 * the scope class and the observed counts so the orchestrator can
 * embed them in the decomposition-proposal comment.
 *
 * The parser is deliberately tolerant:
 *
 * - Acceptance-criteria checkboxes — lines matching `- [ ]` or
 *   `- [x]` (case-insensitive) under a `## Acceptance Criteria`
 *   heading (or any heading whose normalized text equals
 *   `acceptance criteria`). Stops at the next heading.
 * - Sources — bullet list items (`- ` or `* `) under any of
 *   `## Inputs`, `## References`, `## Sources` (case-insensitive).
 *   Counts from every matching section are summed, so an issue
 *   that uses both `## Inputs` and `## References` has its sources
 *   counted across both.
 *
 * An issue with **no** `## Acceptance Criteria` section produces an
 * `acCount` of 0 and is classified `small` on the AC axis. An issue
 * body that's pure prose with no recognizable sections classifies
 * `small` — the gate never rejects an issue it can't read.
 */
export function classifyIssueScope(
  body: string,
  gate: ResolvedScopeGate,
  labels: ReadonlyArray<string> = [],
): {
  readonly scope: ScopeClass;
  readonly acCount: number;
  readonly sourcesCount: number;
  readonly matchedLabel?: string;
} {
  const acCount = countAcceptanceCriteria(body);
  const sourcesCount = countSources(body);
  const effective = resolveOverrideForLabels(gate, labels);
  const scope = resolveScopeClass(acCount, sourcesCount, effective);
  return {
    scope,
    acCount,
    sourcesCount,
    matchedLabel: effective.matchedLabel,
  };
}

/**
 * Render the markdown subsection appended to the
 * `orchestrator-conventions` rule. Always returns a non-empty string
 * so the orchestrator rule documents the scope gate even when the
 * consumer relies on the defaults.
 *
 * When `excludeBundles` is non-empty, per-phase override rows whose
 * phase label is owned by an excluded bundle are dropped from the
 * **Per-phase-label thresholds** sub-table. The sub-section as a
 * whole is hidden when no rows survive.
 */
export function renderScopeGateSection(
  gate: ResolvedScopeGate,
  excludeBundles: ReadonlyArray<string> = [],
): string {
  const { acceptanceCriteria: ac, sources } = gate;

  const lines: Array<string> = [
    "## Scope gate",
    "",
    "The orchestrator enforces a **scope gate** at dispatch. Oversized",
    "issues are rejected, flagged with `status:needs-attention`, and",
    "decomposed into phased sub-issues instead of consuming a",
    "multi-hour worker session. The gate reads the issue body only —",
    "it does not inspect the repository.",
    "",
  ];

  if (!gate.enabled) {
    lines.push(
      "**The scope gate is disabled for this project.** The orchestrator",
      "dispatches every ready issue regardless of size. Enable the gate",
      "via `AgentConfigOptions.scopeGate.enabled = true` once the queue",
      "surfaces consistently sized issues.",
      "",
    );
    return lines.join("\n");
  }

  lines.push(
    "### Classification",
    "",
    "Each issue is scored on two signals read from its body:",
    "",
    "1. **Acceptance criteria** — count of checkbox lines",
    "   (`- [ ]` / `- [x]`) under the `## Acceptance Criteria` heading.",
    "2. **Sources** — count of bullet list items (`- ` / `* `) under",
    "   any of `## Inputs`, `## References`, `## Sources`. Counts",
    "   from every matching section are summed.",
    "",
    "| Class | Acceptance-criteria count | Sources count | Dispatch |",
    "|-------|---------------------------|---------------|----------|",
    `| **small** | ≤ ${ac.smallMax} | ≤ ${sources.smallMax} | yes — worker claims normally |`,
    `| **medium** | ≤ ${ac.mediumMax} | ≤ ${sources.mediumMax} | yes — worker claims normally |`,
    `| **large** | > ${ac.mediumMax} on either axis | > ${sources.mediumMax} on either axis | **no — decompose first** |`,
    "",
    "Both axes must clear the `small` threshold for the issue to classify",
    "as `small`; both must clear the `medium` threshold for `medium`.",
    "An issue with no recognizable `## Acceptance Criteria` section",
    "counts as zero on that axis — the gate never rejects an issue it",
    "cannot read.",
    "",
    "### Handling a `large` issue",
    "",
    "When the classifier returns `large`:",
    "",
    "1. **Add** the `status:needs-attention` label to the issue with",
    "   `--add-label` alone. Leave the existing base `status:*` label",
    "   (`status:ready`) in place — `status:needs-attention` is",
    "   **additive**, so a flagged issue reads `status:ready` +",
    "   `status:needs-attention`. Do **not** remove the base status:",
    "   stripping it leaves the issue with no base `status:*` label,",
    "   invisible to every status-driven query and to the queue itself,",
    "   and a later flag-clear can never restore dispatchability. The",
    "   `status:needs-attention` label already keeps the issue out of",
    "   the dispatch queue, so the base label safely stays:",
    "   ```bash",
    '   gh issue edit <number> --add-label "status:needs-attention"',
    "   ```",
    "2. Post the decomposition-proposal comment (see template below),",
    "   with `<AC_COUNT>` / `<SOURCES_COUNT>` substituted from the",
    "   classification and `<AC_LIMIT>` / `<SOURCES_LIMIT>` set to",
    `   \`${ac.mediumMax}\` / \`${sources.mediumMax}\`.`,
    "3. Skip the issue this dispatch cycle — do **not** claim the",
    "   issue, create a branch, or start implementation.",
  );

  if (gate.autoFile) {
    lines.push(
      "4. **Auto-file is ON.** After posting the comment, file each",
      "   phase from the proposal as a new sub-issue, link them with",
      "   `Depends on:` lines, and update the parent body to include",
      "   `Depends on: #<phase-1>, #<phase-2>, #<phase-3>`. Set the",
      "   parent to `status:blocked`.",
    );
  } else {
    lines.push(
      "4. **Auto-file is OFF.** Stop after posting the comment. A human",
      "   reviews the proposal, files the phased sub-issues manually,",
      "   and clears `status:needs-attention` when the decomposition",
      "   is in place. When clearing the flag, if the issue carries **no**",
      "   base `status:*` label — a legacy issue flagged before the",
      "   additive-flag fix, whose base status was stripped at flag time —",
      "   also add `status:ready` (or `status:blocked` when the body still",
      "   declares an open `Depends on:` dependency) in the same edit so",
      "   the issue re-enters the queue instead of going invisible.",
    );
  }

  const overrideEntries = Object.entries(gate.bundleOverrides).filter(
    ([label]) => !isPhaseLabelOwnedByExcluded(label, excludeBundles),
  );
  if (overrideEntries.length > 0) {
    lines.push(
      "",
      "### Per-phase-label thresholds",
      "",
      "Issues whose `type:*` or phase label matches an entry below are",
      "classified against the override's thresholds instead of the",
      "global `acceptance-criteria` / `sources` defaults. This",
      "accommodates **content-spec workflows** — issues whose AC list",
      "is the per-section content checklist for one cohesive document,",
      "not a phase-completion checklist that can be decomposed.",
      "",
      "| Phase label | AC `mediumMax` | Sources `mediumMax` |",
      "|-------------|----------------|---------------------|",
    );
    // Sort keys for deterministic rendering.
    const sortedKeys = overrideEntries.map(([k]) => k).sort();
    for (const label of sortedKeys) {
      const override = gate.bundleOverrides[label];
      const acMax = override.acceptanceCriteria?.mediumMax;
      const sourcesMax = override.sources?.mediumMax;
      const acCell = acMax === undefined ? "_(global)_" : `${acMax}`;
      const sourcesCell =
        sourcesMax === undefined ? "_(global)_" : `${sourcesMax}`;
      lines.push(`| \`${label}\` | ${acCell} | ${sourcesCell} |`);
    }
    lines.push(
      "",
      "When an issue carries multiple labels that each match an entry",
      "in this table, the orchestrator picks the **first match in",
      "alphabetical order on the label name** — first-wins, deterministic.",
    );
  }

  lines.push(
    "",
    "### Reconfiguring the thresholds",
    "",
    "Every threshold above is configurable per repo — the global",
    "`acceptance-criteria` / `sources` classification bands and each",
    "per-phase-label override. Retune them in the project's",
    "configulator config so the gate fits this repo's issue shapes:",
    "",
    "- `AgentConfigOptions.scopeGate.acceptanceCriteria` /",
    "  `AgentConfigOptions.scopeGate.sources` — the global",
    "  `smallMax` / `mediumMax` bands applied to every issue.",
    "- `AgentConfigOptions.scopeGate.bundleOverrides.<phase-label>` —",
    "  a per-phase-label override for **any** phase label, not just",
    "  the ones shipped above. The map deep-merges with the shipped",
    "  defaults (consumer-wins-per-key): add a key to raise a new",
    "  label's AC / sources ceiling, set an existing key to replace a",
    "  shipped default, or set a key to `undefined` to drop a shipped",
    "  default and classify that label against the global bands.",
    "",
    "Each override entry's `acceptanceCriteria` and `sources` axes are",
    "independent — override one and the other falls through to the",
    "global band. Malformed thresholds (negative, non-integer, or",
    "`mediumMax <= smallMax`) fail the build at synth time.",
  );

  lines.push(
    "",
    "### Decomposition-proposal template",
    "",
    "The orchestrator posts the following comment on every `large`",
    "issue. Consumer repos may override the body via",
    "`AgentConfigOptions.scopeGate.decompositionTemplate`.",
    "",
    "```markdown",
    gate.decompositionTemplate,
    "```",
  );

  return lines.join("\n");
}

/**
 * Render a shell-script snippet embedded in `check-blocked.sh`. The
 * snippet declares a `scope_of()` function that reads an issue body
 * (passed as `$1`) and echoes one of `small` / `medium` / `large`.
 * Orchestrator-side bash hooks call this function before dispatch.
 *
 * Returns the body of a shell function block (including the
 * `scope_of()` wrapper) so the surrounding script can splice it
 * inline at the exact indent level it wants.
 */
export function renderScopeGateShellHelpers(gate: ResolvedScopeGate): string {
  const { acceptanceCriteria: ac, sources } = gate;

  // The POSIX shell function below:
  // 1. Reads an issue body from stdin.
  // 2. Counts checkbox lines under "## Acceptance Criteria".
  // 3. Counts bullet lines under "## Inputs" / "## References" /
  //    "## Sources".
  // 4. Echoes "small" / "medium" / "large" on stdout.
  //
  // The counts are emitted on stderr as `ac=<n> sources=<n>` so the
  // caller can embed them in the decomposition-proposal comment
  // without re-parsing the body.
  //
  // Effective thresholds are read from four shell variables so the
  // caller can override them per-issue based on phase-label matches
  // (see `bundle_override_for()` for the lookup helper).
  // Defaults preserve the global thresholds when the caller does
  // not set them.
  const lines: Array<string> = [
    "# Classify an issue body against the scope-gate thresholds.",
    "# Reads the issue body from stdin. Echoes one of 'small',",
    "# 'medium', or 'large' on stdout. Writes the observed counts to",
    "# stderr as `ac=<n> sources=<n>` so the caller can embed them in",
    "# the decomposition-proposal comment.",
    "#",
    "# Effective per-issue thresholds are read from the four",
    "# `_EFFECTIVE_*` shell variables. The caller is expected to set",
    "# them via `bundle_override_for()` before invoking scope_of();",
    "# defaults preserve the global thresholds.",
    `_EFFECTIVE_AC_SMALL_MAX="\${_EFFECTIVE_AC_SMALL_MAX:-${ac.smallMax}}"`,
    `_EFFECTIVE_AC_MEDIUM_MAX="\${_EFFECTIVE_AC_MEDIUM_MAX:-${ac.mediumMax}}"`,
    `_EFFECTIVE_SOURCES_SMALL_MAX="\${_EFFECTIVE_SOURCES_SMALL_MAX:-${sources.smallMax}}"`,
    `_EFFECTIVE_SOURCES_MEDIUM_MAX="\${_EFFECTIVE_SOURCES_MEDIUM_MAX:-${sources.mediumMax}}"`,
    "scope_of() {",
    "  local body",
    "  body=$(cat)",
    "  local ac_count sources_count",
    "  ac_count=$(printf '%s\\n' \"$body\" | awk '",
    "    # Count only TOP-LEVEL checkboxes — those at the shallowest",
    "    # indentation in each Acceptance-Criteria section. A checkbox",
    "    # with nested/indented sub-checkboxes (e.g. a `file N action-",
    "    # item issues` criterion) counts once, mirroring the TS",
    "    # countTopLevelCheckboxes() helper.",
    "    function flush() {",
    "      if (n > 0) { for (i = 0; i < n; i++) if (indents[i] == mini) total++ }",
    "      n = 0; mini = -1",
    "    }",
    "    BEGIN { in_ac=0; total=0; n=0; mini=-1 }",
    "    # Fully case-insensitive heading match mirrors the TypeScript",
    "    # classifier (/^## acceptance criteria\\s*$/i); POSIX awk has",
    "    # no /i flag, so we compare via tolower() instead of a",
    "    # per-letter character class like [Aa]cceptance [Cc]riteria",
    "    # which would drift for headings like `## ACCEPTANCE CRITERIA`.",
    "    { lower = tolower($0) }",
    "    lower ~ /^## acceptance criteria[[:space:]]*$/ { flush(); in_ac=1; next }",
    "    /^## / { if (in_ac) { flush(); in_ac=0 } }",
    "    in_ac && /^[[:space:]]*-[[:space:]]*\\[[ xX]\\]/ {",
    "      match($0, /^[[:space:]]*/); ind = RLENGTH;",
    "      indents[n++] = ind;",
    "      if (mini < 0 || ind < mini) mini = ind;",
    "    }",
    "    END { flush(); print total }",
    "  ')",
    "  sources_count=$(printf '%s\\n' \"$body\" | awk '",
    "    BEGIN { in_src=0; count=0 }",
    "    # Fully case-insensitive heading match mirrors the TypeScript",
    "    # classifier; see the `ac_count` block above for rationale.",
    "    { lower = tolower($0) }",
    "    lower ~ /^## (inputs|references|sources)[[:space:]]*$/ { in_src=1; next }",
    "    /^## / { in_src=0 }",
    "    # Exclude `Key: value` metadata bullets (e.g. `- Basename: …`)",
    "    # — a bullet whose text is a `Word:` key/value pair is",
    "    # metadata, not a source. Mirrors the TS countSourceBullets()",
    "    # METADATA_KEY_VALUE_REGEX exclusion.",
    "    in_src && /^[[:space:]]*[-*][[:space:]]+/ {",
    "      rest = $0;",
    '      sub(/^[[:space:]]*[-*][[:space:]]+/, "", rest);',
    "      if (rest ~ /^[A-Za-z][A-Za-z0-9-]*:[[:space:]]+[^[:space:]]/) next;",
    "      count++;",
    "    }",
    "    END { print count }",
    "  ')",
    '  printf \'ac=%s sources=%s\\n\' "$ac_count" "$sources_count" >&2',
    '  if [ "$ac_count" -le "$_EFFECTIVE_AC_SMALL_MAX" ] && [ "$sources_count" -le "$_EFFECTIVE_SOURCES_SMALL_MAX" ]; then',
    "    echo small",
    '  elif [ "$ac_count" -le "$_EFFECTIVE_AC_MEDIUM_MAX" ] && [ "$sources_count" -le "$_EFFECTIVE_SOURCES_MEDIUM_MAX" ]; then',
    "    echo medium",
    "  else",
    "    echo large",
    "  fi",
    "}",
    "",
    "# Resolve per-phase-label threshold overrides. Reads a newline-",
    "# separated list of label names on stdin and echoes effective",
    "# threshold variable assignments (one per line) on stdout. The",
    "# caller `eval`s the output to set the four `_EFFECTIVE_*`",
    "# variables before calling `scope_of()`. Also echoes",
    "# `MATCHED_LABEL=<label>` (or `MATCHED_LABEL=`) so the caller can",
    "# report which override fired.",
    "#",
    "# Tie-breaking: when more than one label matches an override key,",
    "# the alphabetical first-wins rule applies — the same rule the",
    "# TypeScript classifier uses.",
    "bundle_override_for() {",
    `  local default_ac_small=${ac.smallMax}`,
    `  local default_ac_medium=${ac.mediumMax}`,
    `  local default_sources_small=${sources.smallMax}`,
    `  local default_sources_medium=${sources.mediumMax}`,
    "  # Sort labels alphabetically so the first match is deterministic.",
    "  local sorted_labels",
    "  sorted_labels=$(LC_ALL=C sort)",
    "  local matched_label=''",
    "  local ac_small=$default_ac_small",
    "  local ac_medium=$default_ac_medium",
    "  local sources_small=$default_sources_small",
    "  local sources_medium=$default_sources_medium",
    "  while IFS= read -r label; do",
    '    [ -z "$label" ] && continue',
    `    case "$label" in`,
  ];

  // Emit one case branch per shipped/resolved override.
  // Sort keys alphabetically so the rendered shell mirrors the
  // alphabetical first-wins rule.
  const sortedKeys = Object.keys(gate.bundleOverrides).sort();
  if (sortedKeys.length === 0) {
    // Emit a no-op case so the case statement is syntactically valid.
    lines.push("      *) ;;");
  } else {
    for (const label of sortedKeys) {
      const override = gate.bundleOverrides[label];
      const acThresholds = override.acceptanceCriteria;
      const sourcesThresholds = override.sources;
      // POSIX shell case patterns require careful escaping; phase
      // labels in this codebase contain only `:`, `-`, lower-case
      // letters, and digits (e.g. `req:write`, `bcm:scaffold`), so
      // a single-quoted literal pattern is safe.
      const branchLines: Array<string> = [`      '${label}')`];
      if (acThresholds !== undefined) {
        branchLines.push(`        ac_small=${acThresholds.smallMax}`);
        branchLines.push(`        ac_medium=${acThresholds.mediumMax}`);
      }
      if (sourcesThresholds !== undefined) {
        branchLines.push(`        sources_small=${sourcesThresholds.smallMax}`);
        branchLines.push(
          `        sources_medium=${sourcesThresholds.mediumMax}`,
        );
      }
      branchLines.push(`        matched_label='${label}'`);
      branchLines.push("        break");
      branchLines.push("        ;;");
      lines.push(...branchLines);
    }
  }

  lines.push(
    "    esac",
    `  done <<<"$sorted_labels"`,
    `  printf 'MATCHED_LABEL=%s\\n' "$matched_label"`,
    `  printf '_EFFECTIVE_AC_SMALL_MAX=%s\\n' "$ac_small"`,
    `  printf '_EFFECTIVE_AC_MEDIUM_MAX=%s\\n' "$ac_medium"`,
    `  printf '_EFFECTIVE_SOURCES_SMALL_MAX=%s\\n' "$sources_small"`,
    `  printf '_EFFECTIVE_SOURCES_MEDIUM_MAX=%s\\n' "$sources_medium"`,
    "}",
  );

  return lines.join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function resolveThresholds(
  supplied: ScopeGateThresholds | undefined,
  defaults: ScopeGateThresholds,
  field: "acceptanceCriteria" | "sources",
): ScopeGateThresholds {
  const merged: ScopeGateThresholds = {
    smallMax: supplied?.smallMax ?? defaults.smallMax,
    mediumMax: supplied?.mediumMax ?? defaults.mediumMax,
  };
  assertValidThresholds(merged, field);
  return merged;
}

/**
 * Deep-merge the consumer's `bundleOverrides` over the shipped
 * defaults with consumer-wins-per-key.
 *
 * - Setting a key to a defined override replaces the shipped
 *   default for that key.
 * - Setting a key to `undefined` opts out of the shipped default.
 * - Adding a new key extends the override map without touching
 *   the shipped defaults.
 *
 * Resolved entries that carry no axis (both `acceptanceCriteria`
 * and `sources` are absent) are dropped so `resolveOverrideForLabels`
 * can assume any entry is actionable.
 *
 * Each axis is validated against the same rules as the global
 * thresholds — negative, non-integer, or inverted ranges throw.
 */
function resolveBundleOverrides(
  supplied:
    | { readonly [phaseLabel: string]: ScopeGateBundleOverride | undefined }
    | undefined,
  defaults: { readonly [phaseLabel: string]: ResolvedScopeGateBundleOverride },
): { readonly [phaseLabel: string]: ResolvedScopeGateBundleOverride } {
  const out: {
    [phaseLabel: string]: ResolvedScopeGateBundleOverride;
  } = {};

  // Start with the shipped defaults so untouched keys carry through.
  for (const [label, override] of Object.entries(defaults)) {
    out[label] = override;
  }

  if (supplied !== undefined) {
    for (const label of Object.keys(supplied)) {
      const value = supplied[label];
      if (value === undefined) {
        // Opt-out — drop the shipped default (or any earlier entry).
        delete out[label];
        continue;
      }

      const resolved: {
        acceptanceCriteria?: ScopeGateThresholds;
        sources?: ScopeGateThresholds;
      } = {};
      if (value.acceptanceCriteria !== undefined) {
        resolved.acceptanceCriteria = resolveThresholds(
          value.acceptanceCriteria,
          DEFAULT_AC_THRESHOLDS,
          "acceptanceCriteria",
        );
      }
      if (value.sources !== undefined) {
        resolved.sources = resolveThresholds(
          value.sources,
          DEFAULT_SOURCES_THRESHOLDS,
          "sources",
        );
      }

      // Drop empty overrides — they would otherwise mask a shipped
      // default with a no-op entry.
      if (
        resolved.acceptanceCriteria === undefined &&
        resolved.sources === undefined
      ) {
        delete out[label];
        continue;
      }

      out[label] = resolved;
    }
  }

  return out;
}

function assertValidThresholds(
  thresholds: ScopeGateThresholds,
  field: "acceptanceCriteria" | "sources",
): void {
  const { smallMax, mediumMax } = thresholds;
  for (const [key, value] of [
    ["smallMax", smallMax],
    ["mediumMax", mediumMax],
  ] as const) {
    if (!Number.isInteger(value)) {
      throw new Error(
        `ScopeGateConfig.${field}.${key} must be a non-negative integer; got ${value}`,
      );
    }
    if (value < 0) {
      throw new Error(
        `ScopeGateConfig.${field}.${key} must be a non-negative integer; got ${value}`,
      );
    }
  }
  if (mediumMax <= smallMax) {
    throw new Error(
      `ScopeGateConfig.${field} requires mediumMax > smallMax; got smallMax=${smallMax}, mediumMax=${mediumMax}`,
    );
  }
}

function resolveScopeClass(
  acCount: number,
  sourcesCount: number,
  thresholds: {
    readonly acceptanceCriteria: ScopeGateThresholds;
    readonly sources: ScopeGateThresholds;
  },
): ScopeClass {
  const { acceptanceCriteria: ac, sources } = thresholds;
  if (acCount <= ac.smallMax && sourcesCount <= sources.smallMax) {
    return "small";
  }
  if (acCount <= ac.mediumMax && sourcesCount <= sources.mediumMax) {
    return "medium";
  }
  return "large";
}

const AC_CHECKBOX_REGEX = /^(\s*)-\s*\[[ xX]\]/;
const SOURCES_BULLET_REGEX = /^\s*[-*]\s+(.*)$/;
/**
 * A `Word: value` bullet body is treated as a key/value metadata
 * pair, not a source. The key is a single token — a letter followed
 * by letters/digits/hyphens — then a colon and at least one space
 * (e.g. `Basename: foo`, `Partition: 2026-Q1`). Requiring a
 * space-free single-token key keeps prose bullets that merely contain
 * a mid-sentence colon (`See section 3: details`) from being dropped,
 * and URLs (`https://…`) are excluded because `:` is followed by `/`,
 * not whitespace.
 */
const METADATA_KEY_VALUE_REGEX = /^[A-Za-z][A-Za-z0-9-]*:\s+\S/;

/**
 * Count the **top-level** checkbox lines under the first
 * `## Acceptance Criteria` heading in `body`. Returns 0 when the
 * heading is absent.
 *
 * Matches `- [ ]` / `- [x]` / `- [X]` (case-insensitive). Only
 * checkboxes at the shallowest indentation seen in the section are
 * counted — a checkbox with nested/indented sub-checkboxes (e.g. a
 * "file N action-item issues" criterion that lists each action item
 * as its own indented checkbox) counts as **1**, not 1+N. This keeps
 * single-deliverable issues with nested criteria from tripping the
 * scope gate.
 */
function countAcceptanceCriteria(body: string): number {
  return countSectionMatches(
    body,
    /^## acceptance criteria\s*$/i,
    countTopLevelCheckboxes,
  );
}

/**
 * Count the source bullet list items under every `## Inputs`,
 * `## References`, or `## Sources` heading in `body`. Counts are
 * summed across all matching sections.
 *
 * `Key: value` metadata bullets (e.g. `- Basename: …`,
 * `- Partition: …`) are excluded — a bullet whose text is a `Word:`
 * key/value pair describes the session, not a source, and would
 * otherwise over-count the sources axis.
 */
function countSources(body: string): number {
  return countSectionMatches(
    body,
    /^## (?:inputs|references|sources)\s*$/i,
    countSourceBullets,
  );
}

/**
 * Count checkboxes whose indentation equals the minimum indentation
 * of any checkbox in `sectionLines`. Nested checkboxes (deeper
 * indentation) are skipped so a parent criterion with sub-items
 * counts once.
 */
function countTopLevelCheckboxes(sectionLines: ReadonlyArray<string>): number {
  let minIndent = Infinity;
  const indents: Array<number> = [];
  for (const line of sectionLines) {
    const match = AC_CHECKBOX_REGEX.exec(line);
    if (match === null) {
      continue;
    }
    const indent = match[1].length;
    indents.push(indent);
    if (indent < minIndent) {
      minIndent = indent;
    }
  }
  return indents.filter((indent) => indent === minIndent).length;
}

/**
 * Count bullet list items that name a source, skipping `Key: value`
 * metadata bullets.
 */
function countSourceBullets(sectionLines: ReadonlyArray<string>): number {
  let count = 0;
  for (const line of sectionLines) {
    const match = SOURCES_BULLET_REGEX.exec(line);
    if (match === null) {
      continue;
    }
    if (METADATA_KEY_VALUE_REGEX.test(match[1])) {
      continue;
    }
    count += 1;
  }
  return count;
}

/**
 * Collect the lines belonging to every section whose heading matches
 * `headingRegex` (summed across sections) and delegate the count to
 * `lineCounter`. A section runs from its heading to the next `## `
 * heading. Centralising the section-walk keeps the AC axis (top-level
 * checkboxes) and the sources axis (non-metadata bullets) on one
 * heading-detection codepath while letting each axis apply its own
 * line rule.
 */
function countSectionMatches(
  body: string,
  headingRegex: RegExp,
  lineCounter: (sectionLines: ReadonlyArray<string>) => number,
): number {
  const lines = body.split(/\r?\n/);
  let count = 0;
  let inSection = false;
  let sectionLines: Array<string> = [];
  const flush = (): void => {
    if (sectionLines.length > 0) {
      count += lineCounter(sectionLines);
      sectionLines = [];
    }
  };
  for (const line of lines) {
    if (headingRegex.test(line)) {
      flush();
      inSection = true;
      continue;
    }
    if (inSection && /^## /.test(line)) {
      flush();
      inSection = false;
      continue;
    }
    if (inSection) {
      sectionLines.push(line);
    }
  }
  flush();
  return count;
}
